import { useEffect, useState, type ReactElement } from "react";

/**
 * The World's face at the head of the World rail — the place's own mark
 * seated first in the window's topmost row, before the operating-mode doors.
 * A World wearing an uploaded mark of its own (LG4: the brand fold's signed
 * URL, supplied by the caller) shows that mark; every other state — no World
 * entered, no mark finalized, mark still resolving, or a broken image —
 * falls back to the estate's Neucleos wordmark: the same theme-aware pair
 * the status footer wears. Local — and a membership that will not resolve —
 * enters no World, so the mark then carries no World identity: it is only
 * the estate's own face.
 *
 * Pure like the rail that seats it: the caller supplies the identity and the
 * resolved mark URL, so the window's titlebar can be rendered whole in a
 * test. The only local state is the broken-image latch.
 */
export function WorldMark({
  name,
  worldKey,
  markUrl = null,
}: {
  /** The entered World's display name; null when no World is entered. */
  readonly name: string | null;
  /** The entered World's key; null in Local and while a membership is unresolved. */
  readonly worldKey: string | null;
  /** The World's finalized mark as a resolved (signed) URL; null when unset. */
  readonly markUrl?: string | null;
}): ReactElement {
  const entered = worldKey !== null;
  const alt = entered ? (name ?? worldKey) : "Neucleos";
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
  useEffect(() => {
    // A re-resolved URL gets a fresh chance; the latch is per exact URL.
    setBrokenUrl(null);
  }, [markUrl]);
  const wearsOwnMark = entered && markUrl !== null && markUrl !== brokenUrl;
  return (
    <span
      className="flex shrink-0 items-center pr-1"
      data-cleo-world-identity={worldKey ?? undefined}
      data-cleo-world-mark=""
      data-cleo-world-mark-custom={wearsOwnMark ? "" : undefined}
      title={entered ? `${alt} — this World` : "Neucleos — no World entered"}
    >
      {wearsOwnMark ? (
        <img
          alt={alt}
          className="block h-4 w-auto max-w-32 object-contain"
          draggable={false}
          src={markUrl}
          onError={() => setBrokenUrl(markUrl)}
        />
      ) : (
        <>
          <img
            alt={alt}
            className="hidden h-4 w-auto dark:block"
            draggable={false}
            src="/brand/neucleos-logo-dark.png"
          />
          <img
            alt={alt}
            className="block h-4 w-auto dark:hidden"
            draggable={false}
            src="/brand/neucleos-logo-light.png"
          />
        </>
      )}
    </span>
  );
}
